import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { OidcSecurityService, LoginResponse } from 'angular-auth-oidc-client';
import { Observable, of } from 'rxjs';
import { tap, catchError, map, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UserStore } from '../store/user/user.store';

/**
 * Consolidated authentication service that handles all auth operations
 * using the angular-auth-oidc-client library
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Private field to cache the observable
  private isAuthenticated$Cache: Observable<boolean> | null = null;

  // Inject dependencies
  private oidcSecurityService = inject(OidcSecurityService);
  private userStore = inject(UserStore);

  constructor() {
    // Create the observable in the constructor (within injection context)
    this.isAuthenticated$Cache = toObservable(this.userStore.isAuthenticated);
  }

  /**
   * Initialize the authentication service
   * This checks the current auth state and updates user information
   * Returns an observable that completes when initialization is done
   */
  init(): Observable<boolean> {
    // Check if we're on the callback route or have auth-related URL parameters
    const hasAuthParams = window.location.search.includes('code=') ||
                         window.location.search.includes('error=') ||
                         window.location.pathname.includes('/auth/callback');

    if (hasAuthParams) {
      // Only run the full authentication check if we're in an auth flow
      return this.checkAuth().pipe(
        tap(({ userData, isAuthenticated }) => {
          if (isAuthenticated && userData) {
            this.userStore.setUser(userData as unknown as Record<string, unknown>);
          }
        }),
        catchError(error => {
          console.error('Error during auth initialization:', error);
          this.userStore.setAuthState(false);
          return of({ isAuthenticated: false } as LoginResponse);
        }),
        map(({ isAuthenticated }) => isAuthenticated)
      );
    } else {
      // For normal page loads, use a safer approach that won't trigger the "no code in url" error
      return new Observable(observer => {
        try {
          // Just set a default non-authenticated state to begin with
          this.userStore.setAuthState(false);

          // Try to get tokens from storage without making any network requests
          this.oidcSecurityService.getAccessToken().pipe(take(1)).subscribe({
            next: token => {
              const isAuthenticated = !!token;
              console.log('Simple token check - has token:', isAuthenticated);
              this.userStore.setAuthState(isAuthenticated);

              if (isAuthenticated) {
                // If we have a token, try to get user data
                this.oidcSecurityService.userData$.pipe(take(1)).subscribe({
                  next: userData => {
                    if (userData) {
                      this.userStore.setUser(userData as unknown as Record<string, unknown>);
                    }
                    observer.next(isAuthenticated);
                    observer.complete();
                  },
                  error: (err) => {
                    console.error('Error getting user data:', err);
                    observer.next(isAuthenticated);
                    observer.complete();
                  }
                });
              } else {
                observer.next(false);
                observer.complete();
              }
            },
            error: (err) => {
              console.error('Error checking token:', err);
              this.userStore.setAuthState(false);
              this.userStore.setUser(null);
              observer.next(false);
              observer.complete();
            }
          });
        } catch (err) {
          console.error('Error during init:', err);
          this.userStore.setAuthState(false);
          observer.next(false);
          observer.complete();
        }
      });
    }
  }

  /**
   * Check if the user is authenticated
   * This is the main method to verify authentication status
   */
  checkAuth(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth().pipe(
      tap(({ isAuthenticated }) => {
        // Update auth state in the UserStore
        this.userStore.setAuthState(isAuthenticated);
      }),
      catchError(error => {
        console.error('Error checking authentication:', error);
        this.userStore.setAuthState(false);
        return of({ isAuthenticated: false } as LoginResponse);
      })
    );
  }

  /**
   * Get the user information as an observable
   */
  user(): Observable<any> {
    return this.oidcSecurityService.userData$;
  }

  /**
   * Get the current user as a promise (for compatibility with older code)
   */
  getUser(): Observable<any> {
    return this.oidcSecurityService.userData$;
  }

  /**
   * Get the current authentication state as a boolean
   */
  get isAuth(): boolean {
    return this.userStore.isAuthenticated();
  }

  /**
   * Get the authentication state as a signal
   */
  get isAuthenticatedSignal() {
    return this.userStore.isAuthenticated;
  }

  /**
   * Get the authentication state as an observable (for backwards compatibility)
   * Returns an observable that emits authentication state changes
   */
  getIsAuthenticated$(): Observable<boolean> {
    if (!this.isAuthenticated$Cache) {
      throw new Error('AuthService not properly initialized');
    }
    return this.isAuthenticated$Cache;
  }

  /**
   * Backwards-compatible observable property alias for authentication state.
   * Allows existing code using `authService.isAuthenticated$` to continue
   * working by delegating to `getIsAuthenticated$()`.
   */
  get isAuthenticated$(): Observable<boolean> {
    return this.getIsAuthenticated$();
  }

  /**
   * Initiate the login process
   */
  login(): void {
    this.oidcSecurityService.authorize();
  }

  /**
   * Handle the authentication callback
   * This processes the response from the identity provider
   */
  handleAuthCallback(): Observable<boolean> {
    return this.checkAuth().pipe(
      map(({ isAuthenticated }) => isAuthenticated)
    );
  }

  /**
   * Complete the authentication process after redirect
   */
  completeAuthentication(): Observable<any> {
    return this.checkAuth().pipe(
      tap(({ userData, isAuthenticated }) => {
        if (isAuthenticated && userData) {
          this.userStore.setUser(userData as unknown as Record<string, unknown>);
        }
      })
    );
  }

  /**
   * Log the user out
   */
  logout(): void {
    // Get the ID token if available, which we'll need for logout hint
    this.oidcSecurityService.getIdToken().pipe(take(1)).subscribe(idToken => {
      // Use the correct Cognito domain from environment configuration
      const cognitoLogoutUrl = `https://${environment.authConfig.domain}`;

      // Clean up user state first
      this.userStore.setAuthState(false);
      this.userStore.setUser(null);

      // First clear the local tokens
      this.oidcSecurityService.logoffLocal();

      // Construct the logout redirect URI - point to home page
      // Must match a redirect URI configured in Cognito app settings and be consistent with environment.appUrl
      const appBaseUrl = (environment as any).appUrl
        ? (environment as any).appUrl.replace(/\/+$/, '')
        : window.location.origin;
      const redirectUri = encodeURIComponent(`${appBaseUrl}/`);

      // Add ID token hint if available (Cognito requires this for optimal logout)
      let logoutUrl = `${cognitoLogoutUrl}/logout?client_id=${environment.authConfig.userPoolWebClientId}&logout_uri=${redirectUri}`;
      if (idToken) {
        logoutUrl += `&id_token_hint=${idToken}`;
      }

      // Navigate to Cognito's logout endpoint
      console.log('Redirecting to Cognito logout URL:', logoutUrl);
      window.location.href = logoutUrl;
    });
  }
}
